export default class VoiceSilenceDetector {

    private static readonly AUTO_STOP_MS = 2500;
    private static readonly CHECK_INTERVAL_MS = 100;
    private static readonly RMS_THRESHOLD = 0.015;

    private intervalId: number|null = null;
    private audioContext: AudioContext|null = null;
    private source: MediaStreamAudioSourceNode|null = null;
    private analyser: AnalyserNode|null = null;
    private silenceStartedAt: number|null = null;
    private speechDetected = false;

    public start(stream: MediaStream, onSilence: () => void, onLevel: (level: number) => void = () => undefined): void {
        this.stop();
        const audioContextConstructor = window.AudioContext || (window as any).webkitAudioContext;
        if (!audioContextConstructor) {
            return;
        }

        try {
            const audioContext = new audioContextConstructor();
            const analyser = audioContext.createAnalyser();
            const source = audioContext.createMediaStreamSource(stream);
            analyser.fftSize = 2048;
            source.connect(analyser);

            const samples = new Uint8Array(analyser.fftSize);
            this.audioContext = audioContext;
            this.source = source;
            this.analyser = analyser;
            this.intervalId = window.setInterval(() => {
                analyser.getByteTimeDomainData(samples);
                const rms = this.calculateRms(samples);
                onLevel(Math.min(1, rms / 0.2));
                if (this.checkSilence(rms)) {
                    onSilence();
                }
            }, VoiceSilenceDetector.CHECK_INTERVAL_MS);
        } catch (error) {
            console.error(error);
        }
    }

    public stop(): void {
        if (this.intervalId !== null) {
            window.clearInterval(this.intervalId);
            this.intervalId = null;
        }

        this.source?.disconnect();
        this.analyser?.disconnect();
        if (this.audioContext !== null && this.audioContext.state !== "closed") {
            void this.audioContext.close();
        }

        this.audioContext = null;
        this.source = null;
        this.analyser = null;
        this.silenceStartedAt = null;
        this.speechDetected = false;
    }

    public calculateRms(samples: Uint8Array): number {
        let sum = 0;
        for (let i = 0; i < samples.length; i++) {
            const sample = samples[i];
            const normalized = (sample - 128) / 128;
            sum += normalized * normalized;
        }

        return Math.sqrt(sum / samples.length);
    }

    private checkSilence(rms: number): boolean {
        const now = Date.now();
        if (rms >= VoiceSilenceDetector.RMS_THRESHOLD) {
            this.speechDetected = true;
            this.silenceStartedAt = null;
            return false;
        }

        if (!this.speechDetected) {
            return false;
        }

        this.silenceStartedAt ??= now;

        return now - this.silenceStartedAt >= VoiceSilenceDetector.AUTO_STOP_MS;
    }

}
